import { CmsMeta } from '../cmsMeta';
import { Link } from 'react-router-dom';
import { useConfig } from '../../../config';
import { useTranslation } from '../../../i18n/i18n';
import { Res, useApi } from '../../../state/api';
import { MetaContainer } from '../../../state/meta';
import { Loop54CategoryProductsRequestBody } from '../../../types';
import { ErrorMessage } from '../../ErrorMessage/ErrorMessage';
import { Loader } from '../../Loader/Loader';
import { ProductListing } from '../../ProductListing/ProductListing';
import { generateUUID, renderTemplate } from '../../utils';
import './CmsProductListCategoryLoop54.css';

type CmsProductListCategoryLoop54Props = {
	categoryId: string;
	moreUrl: string;
	title: string;
	limit?: number;
	offset?: number;
};

export const CmsProductListCategoryLoop54 = ({
	categoryId,
	moreUrl,
	title,
	limit,
	offset,
}: CmsProductListCategoryLoop54Props): JSX.Element => {
	const t = useTranslation().sort;
	const tGeneric = useTranslation().generic;
	const { searchProfileId, randomizeSearchProfileId } = MetaContainer.useContainer();
	const { loop54Api } = useConfig();
	const loop54PostHeaders = {
		'Api-Version': 'V3',
		'User-id': randomizeSearchProfileId ? generateUUID() : searchProfileId,
	};
	const resultsLimit = limit ?? 4;

	const categoryProductsPayload: Loop54CategoryProductsRequestBody = {
		resultsOptions: {
			skip: offset ?? 0,
			take: resultsLimit,
			sortBy: [
				{
					type: 'attribute',
					attributeName: 'boost',
					order: 'desc',
				},
			],
		},
		attribute: {
			name: 'categoryIds',
			value: categoryId,
		},
	};

	const { data: products, error } = useApi(
		Res.categoryIdProductsLoop54,
		{
			categoryId,
			limit: resultsLimit,
			offset: offset ?? 0,
		},
		loop54Api,
		categoryProductsPayload,
		loop54PostHeaders,
		'POST'
	);

	if (error) return <ErrorMessage />;
	if (!products) return <Loader size="line" />;
	if (!products || products.results.count === 0) return <ErrorMessage />;

	return (
		<div className="cms-product-listing">
			<h2>
				{title} <small>({renderTemplate(t.items, { total: products.results.count })})</small>
			</h2>
			<ProductListing products={products.results.items} />
			<div className="text-center view-more-button">
				<Link className="button" to={moreUrl}>
					{tGeneric.showMore}
				</Link>
			</div>
		</div>
	);
};

export const cmsMeta: CmsMeta = {
	description:
		'A row of products from one category, ordered by the `boost` attribute in the pim rather than by date - a boosted product outranks a newer one. `categoryId` is the pim id - look it up rather than guessing, and `moreUrl` is where the row links on.',
	example: [
		'CmsProductListCategoryLoop54',
		{
			index: 2,
			title: 'Tastaturen & Mäuse',
			categoryId: '8116082',
			limit: 4,
			offset: '0',
			moreUrl: '/c/it-elektronik/tastaturen-mause',
		},
	],
};
